import { useState } from 'react';
import { KeyRound, Plug, RefreshCw, ShieldCheck, Unplug, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatRelative } from '@/lib/formatters';
import {
  useAdAccounts,
  useDisconnectIntegration,
  useIntegrations,
  useSetMarketingAppCredentials,
  useSocialAccounts,
  useTriggerSync,
  type SyncFunctionName,
} from '@/features/integrations/hooks';
import {
  marketingRedirectUri,
  startMarketingOAuth,
  type MarketingOAuthProvider,
  type MarketingProviderConfig,
} from '@/features/integrations/marketingOAuth';
import type { Tables } from '@/types/database';

/**
 * One card per marketing OAuth provider on the Integrations page. Shared
 * across the six providers added in migration 57 because they all use the
 * same connection model:
 *
 *   • BYO Client ID + Client Secret (per-org, set via
 *     `<provider>-set-app-credentials`).
 *   • Per-account OAuth dance — the operator picks the account during the
 *     consent screen; the matching callback page (`<Provider>CallbackPage`)
 *     provisions the row in `ad_accounts` or `social_accounts`.
 *   • One-button sync now / disconnect, plus a per-account list under the
 *     fold.
 *
 * The "kind" prop drives the right account-list hook (`ad_accounts` vs
 * `social_accounts`) and the surrounding copy (campaigns vs posts).
 */
export interface MarketingOAuthCardProps {
  provider: MarketingProviderConfig;
  /** Identifies which account-list table to render under the card body. */
  kind: 'ads' | 'social';
  /** Card-header icon, e.g. <BarChart3 />. */
  icon: LucideIcon;
  /**
   * Sync Edge Function name (e.g. 'meta-ads-sync'). The matching cron
   * already lives in migration 57; this is just the on-demand "Sync now"
   * trigger.
   */
  syncFn: SyncFunctionName;
  /**
   * Some providers (Google Ads) require an extra public token beyond the
   * Client ID / Client Secret pair — pass the field key + label here and
   * the configure-credentials dialog will surface a third input.
   */
  extraCredential?: {
    key: 'developer_token';
    label: string;
    helpText: string;
  };
  /**
   * Human-readable "where to find these" pointer rendered inside the
   * configure-credentials dialog. Keep it short (one sentence) — full
   * docs belong in ROADMAP §2.2 / §2.3.
   */
  appDocsLabel: string;
  appDocsHref: string;
  /** Inline help shown below the dialog header. */
  appDocsHint: string;
  /**
   * What account-level identifier the operator picks during OAuth. Shown
   * in the empty-state copy ("No Facebook pages connected yet").
   */
  accountNoun: { singular: string; plural: string };
}

export function MarketingOAuthCard({
  provider,
  kind,
  icon: Icon,
  syncFn,
  extraCredential,
  appDocsLabel,
  appDocsHref,
  appDocsHint,
  accountNoun,
}: MarketingOAuthCardProps) {
  const { data: integrations, isLoading: integrationsLoading } = useIntegrations();
  const integration = integrations?.find((i) => i.provider === provider.id);
  // React's rules-of-hooks force us to call both hooks unconditionally —
  // but `enabled` short-circuits the network round-trip on the unused
  // one, so this costs us a query-key entry, not an extra fetch. The
  // selector at the bottom picks whichever returned data we actually
  // want to render.
  const adAccounts = useAdAccounts(provider.id, { enabled: kind === 'ads' });
  const socialAccounts = useSocialAccounts(provider.id, { enabled: kind === 'social' });
  const accounts: Tables<'ad_accounts' | 'social_accounts'>[] =
    kind === 'ads' ? (adAccounts.data ?? []) : (socialAccounts.data ?? []);
  const accountsLoading =
    kind === 'ads' ? adAccounts.isLoading : socialAccounts.isLoading;

  const setAppCreds = useSetMarketingAppCredentials();
  const sync = useTriggerSync();
  const disconnect = useDisconnectIntegration();

  const [configureOpen, setConfigureOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [extraValue, setExtraValue] = useState('');

  const status = integration?.status ?? 'disconnected';
  const lastSynced = integration?.last_synced_at;
  const lastError = integration?.last_error;
  const metadata = (integration?.metadata ?? {}) as Record<string, unknown>;
  const configuredClientId = (metadata.client_id as string | undefined) ?? '';
  const configuredExtra =
    extraCredential && (metadata[extraCredential.key] as string | undefined);
  const isAppConfigured =
    Boolean(configuredClientId) &&
    (!extraCredential || Boolean(configuredExtra));
  const activeAccounts = accounts.filter((a) => a.is_active);
  const hasAccounts = activeAccounts.length > 0;

  const handleSaveAppCreds = async () => {
    const id = clientId.trim();
    const secret = clientSecret.trim();
    const extra = extraValue.trim();
    if (!id || !secret) {
      toast.error('Client ID and Client secret are required.');
      return;
    }
    if (extraCredential && !extra) {
      toast.error(`${extraCredential.label} is required.`);
      return;
    }
    try {
      await setAppCreds.mutateAsync({
        provider: provider.id,
        client_id: id,
        client_secret: secret,
        developer_token: extraCredential ? extra : undefined,
      });
      toast.success(`${provider.label} app credentials saved.`);
      setClientId('');
      setClientSecret('');
      setExtraValue('');
      setConfigureOpen(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      toast.error(msg);
    }
  };

  const handleStartConnect = () => {
    if (!isAppConfigured) {
      toast.error('Configure app credentials first.');
      return;
    }
    const err = startMarketingOAuth(provider.id, { clientId: configuredClientId });
    if (err) toast.error(err);
    // On success the browser is already navigating away. sessionStorage
    // carries the CSRF state for the callback page to validate.
  };

  const handleSyncNow = async () => {
    try {
      await sync.mutateAsync(syncFn);
      toast.success(`${provider.label} sync complete.`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sync failed';
      toast.error(msg);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync(provider.id);
      toast.success(`${provider.label} disconnected.`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Disconnect failed';
      toast.error(msg);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              {provider.label}
              <MarketingStatusBadge status={status} />
            </CardTitle>
          </div>
          <Icon className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
        </div>
      </CardHeader>
      <CardContent>
        {integrationsLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                OAuth 2.0 ({provider.scope.split(provider.scopeDelimiter).length} scopes)
              </span>
              <span>Last sync: {lastSynced ? formatRelative(lastSynced) : '—'}</span>
              {hasAccounts && (
                <span>
                  {activeAccounts.length}{' '}
                  {activeAccounts.length === 1
                    ? accountNoun.singular
                    : accountNoun.plural}{' '}
                  connected
                </span>
              )}
            </div>

            {!isAppConfigured && (
              <p className="rounded-md border border-dashed border-border/60 bg-card/40 px-3 py-2 text-xs text-muted-foreground">
                <strong>Step 1.</strong> Configure your {provider.label} app's
                Client ID + Client secret from{' '}
                <a
                  href={appDocsHref}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  {appDocsLabel}
                </a>
                . Register{' '}
                <code className="rounded bg-secondary px-1">
                  {marketingRedirectUri(provider.id)}
                </code>{' '}
                as an allowed redirect URI. <strong>Step 2</strong> connects each{' '}
                {accountNoun.singular} via the platform's hosted consent screen.
              </p>
            )}

            {lastError && (
              <p className="rounded-md bg-fresh-salmon/10 px-3 py-2 text-xs text-fresh-salmon-dark">
                {lastError}
              </p>
            )}

            {hasAccounts && (
              <ul className="space-y-1 text-sm">
                {activeAccounts.map((a) => {
                  const cfg = (a.config as Record<string, unknown> | null) ?? {};
                  const handle =
                    'handle' in a && a.handle ? `@${a.handle}` : (cfg.handle as string | undefined);
                  return (
                    <li
                      key={a.id}
                      className="flex items-center justify-between rounded-md border border-border/60 bg-card/40 px-3 py-2"
                    >
                      <span className="font-medium">{a.name}</span>
                      <code className="text-xs text-muted-foreground">
                        {handle ?? a.external_id}
                      </code>
                    </li>
                  );
                })}
              </ul>
            )}
            {accountsLoading && !hasAccounts && (
              <Skeleton className="h-8 w-full" />
            )}

            <div className="flex flex-wrap items-center gap-2">
              <ConfigureCredentialsDialog
                open={configureOpen}
                onOpenChange={setConfigureOpen}
                provider={provider}
                clientId={clientId}
                setClientId={setClientId}
                clientSecret={clientSecret}
                setClientSecret={setClientSecret}
                extraValue={extraValue}
                setExtraValue={setExtraValue}
                extraCredential={extraCredential}
                appDocsLabel={appDocsLabel}
                appDocsHref={appDocsHref}
                appDocsHint={appDocsHint}
                onSave={handleSaveAppCreds}
                saving={setAppCreds.isPending}
                isConfigured={isAppConfigured}
                configuredClientId={configuredClientId}
                configuredExtra={configuredExtra ?? ''}
              />

              <Button
                onClick={handleStartConnect}
                disabled={!isAppConfigured}
                variant={hasAccounts ? 'outline' : 'default'}
              >
                <Plug className="h-4 w-4" />
                {hasAccounts
                  ? `Connect another ${accountNoun.singular}`
                  : `Connect ${aOrAn(accountNoun.singular)}`}
              </Button>

              {hasAccounts && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleSyncNow}
                    disabled={sync.isPending}
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`}
                    />
                    {sync.isPending ? 'Syncing…' : 'Sync now'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDisconnect}
                    disabled={disconnect.isPending}
                  >
                    <Unplug className="h-4 w-4" /> Disconnect
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConfigureCredentialsDialog({
  open,
  onOpenChange,
  provider,
  clientId,
  setClientId,
  clientSecret,
  setClientSecret,
  extraValue,
  setExtraValue,
  extraCredential,
  appDocsLabel,
  appDocsHref,
  appDocsHint,
  onSave,
  saving,
  isConfigured,
  configuredClientId,
  configuredExtra,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: MarketingProviderConfig;
  clientId: string;
  setClientId: (v: string) => void;
  clientSecret: string;
  setClientSecret: (v: string) => void;
  extraValue: string;
  setExtraValue: (v: string) => void;
  extraCredential?: MarketingOAuthCardProps['extraCredential'];
  appDocsLabel: string;
  appDocsHref: string;
  appDocsHint: string;
  onSave: () => void;
  saving: boolean;
  isConfigured: boolean;
  configuredClientId: string;
  configuredExtra: string;
}) {
  const formId = `${provider.id}-app-creds`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant={isConfigured ? 'outline' : 'default'}>
          <KeyRound className="h-4 w-4" />
          {isConfigured ? 'Reconfigure app credentials' : 'Configure app credentials'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{provider.label} app credentials</DialogTitle>
          <DialogDescription>
            From{' '}
            <a
              href={appDocsHref}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2"
            >
              {appDocsLabel}
            </a>
            , copy your <strong>Client ID</strong> and <strong>Client secret</strong>.{' '}
            {appDocsHint} Atlas encrypts the secret before it touches disk; the
            Client ID is non-secret and stored on the integration metadata.
            Register{' '}
            <code className="rounded bg-secondary px-1">
              {marketingRedirectUri(provider.id)}
            </code>{' '}
            as an allowed redirect URI.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor={`${formId}-id`}>Client ID</Label>
            <Input
              id={`${formId}-id`}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={
                isConfigured && configuredClientId
                  ? `Current: ${configuredClientId.slice(0, 16)}…`
                  : `Paste from ${provider.label}`
              }
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${formId}-secret`}>Client secret</Label>
            <Input
              id={`${formId}-secret`}
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={
                isConfigured ? '••••••••••• (re-enter to rotate)' : 'Reveal once in dashboard'
              }
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          {extraCredential && (
            <div className="space-y-2">
              <Label htmlFor={`${formId}-extra`}>{extraCredential.label}</Label>
              <Input
                id={`${formId}-extra`}
                value={extraValue}
                onChange={(e) => setExtraValue(e.target.value)}
                placeholder={
                  isConfigured && configuredExtra
                    ? `Current: ${configuredExtra.slice(0, 12)}…`
                    : extraCredential.label
                }
                autoComplete="off"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">{extraCredential.helpText}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              saving ||
              !clientId.trim() ||
              !clientSecret.trim() ||
              (Boolean(extraCredential) && !extraValue.trim())
            }
          >
            {saving ? 'Saving…' : 'Save credentials'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarketingStatusBadge({ status }: { status: string }) {
  if (status === 'connected') return <Badge variant="success">Connected</Badge>;
  if (status === 'syncing') return <Badge variant="info">Syncing</Badge>;
  if (status === 'error') return <Badge variant="warning">Error</Badge>;
  return <Badge variant="muted">Disconnected</Badge>;
}

// "a Facebook page" / "an Instagram business" — picks the right article so
// the empty-state and the secondary button copy doesn't read like robot text.
function aOrAn(noun: string): string {
  return /^[aeiou]/i.test(noun) ? `an ${noun}` : `a ${noun}`;
}

export type { MarketingOAuthProvider };
